/**
 * AI Assistant chat — conversation list, history, and send.
 *
 * ## One path, because the other one is deleted
 *
 * This hook used to branch on `REACT_APP_AI_BFF_URL`: when set, it streamed
 * through the AI BFF via `@ai-sdk/react`'s `useChat`; when unset, it POSTed to
 * the Python runtime. The BFF is gone — every model call now egresses through
 * the control-plane broker, and the runtime is the only service the browser
 * talks to — so the branch is gone with it.
 *
 * **This costs streaming, deliberately and temporarily.** The broker streams
 * (`POST /api/internal/ai/v1/chat/completions` with `stream: true`, SSE), but
 * the runtime's own `POST /api/ai/chat` does not yet forward it, so replies
 * arrive whole. Restoring token-by-token output means giving that runtime
 * endpoint an SSE mode — tracked as frontend AI alignment work, not something
 * to fake here by re-adding a second backend.
 */
import { useAuth } from '../context/AuthContext';
import { useRef, useEffect, useCallback, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function useAIChat(conversationId) {
    const { getAuthHeader } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [loadingConvs, setLoadingConvs] = useState(true);
    const messagesEndRef = useRef(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [input, setInput] = useState('');
    const [error, setError] = useState(null);
    const [activeConvId, setActiveConvId] = useState(conversationId || null);

    const headers = getAuthHeader();

    // Auto-scroll on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const loadConversations = useCallback(async () => {
        try {
            const res = await fetch(`${BACKEND_URL}/api/ai/conversations`, { headers });
            if (res.ok) setConversations(await res.json());
        } catch {} finally { setLoadingConvs(false); }
    }, [headers]);

    const loadConversation = useCallback(async (convId) => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/ai/conversations/${convId}`, { headers: getAuthHeader() });
            setMessages(res.data.messages || []);
            setActiveConvId(convId);
        } catch {}
    }, [getAuthHeader]);

    const startNewConversation = useCallback(() => {
        setActiveConvId(null);
        setMessages([]);
    }, []);

    const sendMessage = useCallback(async (text) => {
        if (!text?.trim() || loading) return null;

        const userMsg = {
            message_id: `temp-${Date.now()}`,
            id: `temp-${Date.now()}`,
            role: 'user',
            content: text,
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg]);
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
                message: text,
                conversation_id: activeConvId || undefined,
            }, { headers: getAuthHeader() });
            const aiMsg = {
                message_id: `ai-${Date.now()}`,
                id: `ai-${Date.now()}`,
                role: 'assistant',
                content: res.data.response,
                timestamp: new Date().toISOString(),
            };
            setMessages(prev => [...prev, aiMsg]);
            if (!activeConvId) {
                setActiveConvId(res.data.conversation_id);
                loadConversations();
            }
            return res.data.response;
        } catch (err) {
            setMessages(prev => prev.filter(m => m.message_id !== userMsg.message_id));
            // `/api/ai/chat` now answers with a typed error rather than a 200
            // carrying invented content, so the reason can be surfaced instead
            // of the message just vanishing.
            const code = err?.response?.data?.detail?.error;
            setError(
                code === 'feature_not_available' ? 'The AI assistant is not included in your plan.'
                : code === 'spend_cap_exceeded' ? 'Your organization has reached its AI spend limit for this month.'
                : code === 'quota_exceeded' ? 'Too many AI requests right now — try again in a moment.'
                : code === 'org_scope_required' ? 'Your account is not attached to an organization.'
                : 'The AI assistant is unavailable right now.'
            );
            return null;
        } finally { setLoading(false); }
    }, [loading, activeConvId, getAuthHeader, loadConversations]);

    const handleInputChange = useCallback((e) => setInput(e.target.value), []);
    const handleSubmit = useCallback((e) => {
        e?.preventDefault?.();
        if (input.trim()) {
            sendMessage(input);
            setInput('');
        }
    }, [input, sendMessage]);

    return {
        conversations,
        messages,
        activeConvId,
        loading,
        error,
        status: loading ? 'submitted' : 'ready',
        loadingConvs,
        messagesEndRef,
        loadConversations,
        loadConversation,
        startNewConversation,
        sendMessage,
        input,
        handleInputChange,
        handleSubmit,
        // Kept so callers that wire an abort control keep compiling. There is
        // nothing to stop: the request is a single POST, not a stream.
        stop: () => {},
    };
}
